import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AddCommasToAmount } from '../../../helper/Utils';
import DataTable from 'react-data-table-component';
import { ADD_CHALLAN_FAIL } from '../../../RTK/Slices/ChallanSlice';
import {
    getAuthorities,
    getAuthoritieAccounts,
    getLabourcessDetails,
    approveLabour,
} from '../../../helper/Api';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { FormControl, TextField } from '@mui/material';
import { AiOutlineVerticalAlignBottom } from 'react-icons/ai';
import { FiCheckCircle, FiXCircle } from 'react-icons/fi';
import Spinner from '../../../components/Tools/spinner';
import SubHeader from '../../../components/Subheader/Subheader';
import CustomBreadcrumbs from '../../../components/Tools/Breadcumbs';
import ApprovalLabourCessPreview from '../../../components/Modal/ApproveLabourCess';
import LabourPayment from './LabourCessPayment';
import '../../../styles/ApprovalActionButtons.css';
import './ViewLabourCessChallan.css';

const MultiLineHeader = ({ lines, align = 'center' }) => (
    <div className={`labour-cess-col-header labour-cess-col-header--${align}`}>
        {lines.map((line, idx) => (
            <React.Fragment key={idx}>
                {idx > 0 && <br />}
                {line}
            </React.Fragment>
        ))}
    </div>
);

const CenterCell = ({ children }) => (
    <div className="labour-cess-cell-center">{children}</div>
);

const actionIconBtnStyle = {
    width: 28,
    height: 28,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '6px',
    background: 'transparent',
};

const formatAmount = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const getApprovalTypeLabel = (paymentType) => {
    if (paymentType === '1') return 'FULL PAYMENT';
    if (paymentType === '2') return 'PART PAYMENT';
    return '';
};

const getAttachmentUrl = (attachment) => {
    if (!attachment) return '';
    if (typeof attachment === 'string') return attachment;
    return attachment.file_url || attachment.url || '';
};

const ViewLabourCess = () => {
    const { id } = useParams();
    const { authData, financialYears } = useSelector((state) => state.authData);
    const fyList = financialYears.payload.fy_list;
    const fyIdRef = useRef(localStorage.getItem('globalDateId'));
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [approveCodition, setApproveCondition] = useState('');
    const [remarksError, setRemarksError] = useState('');
    const [authorityAccounts, setAuthorityAccounts] = useState([]);
    const [authorities, setAuthorities] = useState([]);
    const [invoiceList, setInvoiceList] = useState([]);
    const [formData, setFormData] = useState({
        id: '',
        invoices: [],
        payment_order_no: '',
        amount: '',
        payment_order_date: '',
        doc_url: '',
        attachments: [],
        authority_id: '',
        authority_account_id: '',
        l2_remarks: '',
        l3_remarks: '',
        bankDetails: null,
    });

    const handlePageTrigger = () => {
        fyIdRef.current = localStorage.getItem('globalDateId');
    };

    const customStyles = {
        table: {
            style: {
                width: '100%',
            },
        },
        headRow: {
            style: {
                minHeight: '52px',
            },
        },
        headCells: {
            style: {
                fontWeight: 400,
                fontSize: '14px',
                whiteSpace: 'normal',
                overflow: 'visible',
                textOverflow: 'clip',
                wordBreak: 'break-word',
                lineHeight: 1.35,
                paddingTop: '8px',
                paddingBottom: '8px',
            },
        },
    };

    const handleDownload = (url) => {
        if (!url) return;
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const columns = [
        {
            name: <MultiLineHeader lines={['Invoice No', 'Invoice Date']} align="left" />,
            selector: (row) => {
                if (row.isFooter) return row.invoice_no;
                const invoiceDate = row.invoice_date
                    ? moment(row.invoice_date, ['YYYY-MM-DD', 'DD-MM-YYYY']).format('DD-MM-YYYY')
                    : '';
                return (
                    <div>
                        <div><b>{row.invoice_no || ''}</b></div>
                        {invoiceDate && <div className="text-muted fs-12px">{invoiceDate}</div>}
                    </div>
                );
            },
            wrap: true,
            minWidth: '130px',
            grow: 1.2,
        },
        {
            name: <MultiLineHeader lines={['Beneficiary', 'Details']} align="left" />,
            selector: (row) => {
                if (row.isFooter) return '';
                return (
                    <div>
                        <div>{row.company_name || ''}</div>
                        {row.pan_no && (
                            <div className="text-muted fs-12px">
                                <b>Pan No.:</b> {row.pan_no}
                            </div>
                        )}
                    </div>
                );
            },
            wrap: true,
            minWidth: '170px',
            grow: 2,
        },
        {
            name: <MultiLineHeader lines={['Approval', 'Type']} align="center" />,
            selector: (row) => (row.isFooter ? '' : getApprovalTypeLabel(row.payment_type)),
            wrap: true,
            minWidth: '110px',
            grow: 1,
            center: true,
        },
        {
            name: <MultiLineHeader lines={['Voucher No', 'Voucher Date']} align="left" />,
            selector: (row) => {
                if (row.isFooter) return '';
                const voucherDate = row.voucher_date
                    ? moment(row.voucher_date, ['YYYY-MM-DD', 'DD-MM-YYYY']).format('DD-MM-YYYY')
                    : '';
                return (
                    <div>
                        <div><b>{row.voucher_no || ''}</b></div>
                        {voucherDate && <div className="text-muted fs-12px">{voucherDate}</div>}
                    </div>
                );
            },
            wrap: true,
            minWidth: '150px',
            grow: 1.5,
        },
        {
            name: <MultiLineHeader lines={['Transaction', 'Date']} align="center" />,
            selector: (row) => {
                if (row.isFooter) return '';
                return row.transaction_date
                    ? moment(row.transaction_date, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD']).format('DD-MM-YYYY')
                    : '';
            },
            minWidth: '105px',
            grow: 1,
            center: true,
        },
        {
            name: <MultiLineHeader lines={['Taxable', 'Amount']} align="center" />,
            selector: (row) => (row.isFooter ? '' : formatAmount(row.taxable_amount)),
            minWidth: '105px',
            grow: 1,
            center: true,
        },
        {
            name: <MultiLineHeader lines={['Invoice', 'Amount']} align="center" />,
            selector: (row) => (row.isFooter ? '' : formatAmount(row.payable_amount)),
            minWidth: '105px',
            grow: 1,
            center: true,
        },
        {
            name: <MultiLineHeader lines={['Sanctioned', 'Amount']} align="center" />,
            selector: (row) => (row.isFooter ? '' : formatAmount(row.sanction_amount)),
            minWidth: '115px',
            grow: 1,
            center: true,
        },
        {
            name: 'Labour Cess',
            selector: (row) => (
                <CenterCell>
                    {row.isFooter ? row.labour_amount : formatAmount(row.labour_amount)}
                </CenterCell>
            ),
            minWidth: '95px',
            grow: 1,
            center: true,
        },
        {
            name: 'Actions',
            selector: (row) => (
                row.isFooter ? null : (
                    <div className="d-flex align-items-center justify-content-center" style={{ gap: '6px', width: '100%' }}>
                        <button
                            type="button"
                            title="Download Attachment"
                            disabled={!row?.invoice}
                            onClick={() => handleDownload(row.invoice)}
                            style={{
                                ...actionIconBtnStyle,
                                border: '1px solid #1976d2',
                                color: '#1976d2',
                                cursor: row?.invoice ? 'pointer' : 'not-allowed',
                                opacity: row?.invoice ? 1 : 0.5,
                            }}
                        >
                            <AiOutlineVerticalAlignBottom size={16} />
                        </button>
                    </div>
                )
            ),
            width: '100px',
            minWidth: '100px',
            grow: 0,
            center: true,
        },
    ];

    const calculateTotalAmount = (propertyName) => {
        let total = 0;
        invoiceList.forEach((row) => {
            const depositAmount = Number(parseFloat(row[propertyName]));
            if (!Number.isNaN(depositAmount)) {
                total += depositAmount;
            }
        });
        return total.toFixed(2);
    };

    const totalLabourCessAmount = calculateTotalAmount('labour_amount');

    const footerRow = {
        isFooter: true,
        invoice_no: <b>Total</b>,
        labour_amount: <b>{AddCommasToAmount(totalLabourCessAmount)}</b>,
    };

    const updatedData = invoiceList.length ? [...invoiceList, footerRow] : [];

    const authorityLabel = authorities.find((item) => String(item.id) === String(formData.authority_id))?.name || '';
    const accountLabel = formData.bankDetails
        ? `${formData.bankDetails.bank_name} [${formData.bankDetails.account_no}]`
        : (() => {
            const account = authorityAccounts.find((item) => String(item.id) === String(formData.authority_account_id));
            return account ? `${account.bank_name} [${account.account_no}]` : '';
        })();

    useEffect(() => {
        fetchAuthorities();
        fetchLabourCessDetail(id);
    }, [id]);

    const fetchAuthorityAccounts = async (authorityId) => {
        try {
            const accounts = await getAuthoritieAccounts(authorityId);
            if (accounts.data.status) {
                setAuthorityAccounts(accounts.data.result);
            }
        } catch (e) {
            setAuthorityAccounts([]);
        }
    };

    const handleChallanFormData = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (remarksError) {
            if (name === 'l2_remarks' && String(value).trim()) {
                setRemarksError('');
            }
            if (name === 'l3_remarks' && String(value).trim()) {
                setRemarksError('');
            }
        }
    };

    const validateRemarks = () => {
        if (authData.user.user_type === 'l2') {
            if (!String(formData.l2_remarks || '').trim()) {
                return 'L2 remarks are required.';
            }
        }
        if (authData.user.user_type === 'l3') {
            if (!String(formData.l3_remarks || '').trim()) {
                return 'L3 remarks are required.';
            }
        }
        return '';
    };

    const previewhandler = (value, action) => {
        const error = validateRemarks();
        if (error) {
            setRemarksError(error);
            toast.error(error, { position: toast.POSITION.TOP_CENTER });
            return;
        }
        setRemarksError('');
        setShowPreview(value);
        setApproveCondition(action);
    };

    const fetchAuthorities = async () => {
        try {
            const accounts = await getAuthorities();
            if (accounts.data.status) {
                setAuthorities(accounts.data.result);
            }
        } catch (e) {
            // ignore
        }
    };

    const fetchLabourCessDetail = async (challanId) => {
        try {
            setLoading(true);
            const details = await getLabourcessDetails(challanId);
            if (details.data.status) {
                setFormData((prevState) => {
                    const updatedFormData = { ...prevState, ...details.data.result };
                    updatedFormData.bankDetails = details.data.result.authorityAccountDetails;
                    updatedFormData.attachments = details.data.result.doc_url
                        ? [details.data.result.doc_url]
                        : [];
                    return updatedFormData;
                });
                if (details.data.result.invoices?.length) {
                    setInvoiceList(details.data.result.invoices);
                }
                fetchAuthorityAccounts(details.data.result.authority_id);
            }
        } catch (e) {
            dispatch(ADD_CHALLAN_FAIL('error getting while get Labour Cess Detail'));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        const error = validateRemarks();
        if (error) {
            setRemarksError(error);
            toast.error(error, { position: toast.POSITION.TOP_CENTER });
            return;
        }

        try {
            setLoading(true);
            let details;
            if (authData.user.user_type === 'l2') {
                details = await approveLabour({
                    payment_order_no: formData.payment_order_no,
                    l2_approval: approveCodition,
                    l2_remarks: formData.l2_remarks,
                });
                setFormData((prevState) => ({
                    ...prevState,
                    l2_approval: approveCodition,
                }));
            } else {
                details = await approveLabour({
                    payment_order_no: formData.payment_order_no,
                    l3_approval: approveCodition,
                    l3_remarks: formData.l3_remarks,
                });
                setFormData((prevState) => ({
                    ...prevState,
                    l3_approval: approveCodition,
                }));
            }
            if (details.data.status) {
                const actionLabel = String(approveCodition) === '1' ? 'approved' : 'rejected';
                toast.success(
                    details.data.message || `Labour cess challan ${actionLabel} successfully`,
                    { position: toast.POSITION.TOP_CENTER }
                );
                setShowPreview(false);
                setApproveCondition('');
                setTimeout(() => {
                    navigate('/royalty_labour_approval');
                }, 400);
            } else {
                toast.error(details.data.message || 'Failed to update approval', {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } catch (error) {
            dispatch(ADD_CHALLAN_FAIL('error getting while updating challan'));
            toast.error(error?.response?.data?.message || 'Failed to update approval', {
                position: toast.POSITION.TOP_CENTER,
            });
        } finally {
            setLoading(false);
        }
    };

    const showApprovalActions = (authData.user.user_type === 'l2' && formData.l2_approval == null)
        || (authData.user.user_type === 'l3' && formData.l3_approval == null);

    const isPaymentView = formData.l2_approval == 1 && formData.l3_approval == 1;

    const linksArray = [
        {
            href: '/royalty_labour_approval',
            label: 'Royalty & Labour Cess',
        },
    ];

    return (
        <div className="view-labour-cess-challan-page">
            {loading && <Spinner />}

            <ApprovalLabourCessPreview
                showPreview={showPreview}
                setShowPreview={setShowPreview}
                formData={formData}
                handleSubmit={handleSubmit}
                invoiceList={invoiceList}
                authorities={authorities}
                authorityAccounts={authorityAccounts}
                approvalAction={approveCodition}
            />

            <SubHeader fy_list={fyList} handlePageTrigger={handlePageTrigger} />

            <div className="row mb-3" style={{ padding: '0 16px' }}>
                <div className="col-md-12">
                    <h4 style={{ color: '#28326f', margin: 0 }}>View Labour Cess</h4>
                    <CustomBreadcrumbs linksArray={linksArray} title="View Labour Cess" />
                </div>
            </div>

            <div className="scheme p-3">
                <div className="card p-3">
                    <div className="view-labour-cess-challan-datatable-root">
                        <DataTable
                            customStyles={customStyles}
                            className="dataTables_wrapper"
                            columns={columns}
                            data={updatedData}
                            fixedHeader
                            fixedHeaderScrollHeight="320px"
                            conditionalRowStyles={[
                                {
                                    when: (row) => row.isFooter,
                                    style: {
                                        backgroundColor: '#f8f9fc',
                                        fontWeight: 'bold',
                                    },
                                },
                            ]}
                        />
                    </div>
                </div>

                <div className="card p-3 mt-3">
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <FormControl fullWidth>
                                <TextField
                                    label="Payment Order No"
                                    variant="outlined"
                                    name="payment_order_no"
                                    value={formData.payment_order_no || ''}
                                    InputProps={{ readOnly: true }}
                                    disabled
                                />
                            </FormControl>
                        </div>

                        <div className="col-md-6 mb-3">
                            <FormControl fullWidth>
                                <TextField
                                    label="Payment Order Date"
                                    variant="outlined"
                                    type="date"
                                    name="payment_order_date"
                                    value={formData.payment_order_date || ''}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{ readOnly: true }}
                                    disabled
                                />
                            </FormControl>
                        </div>

                        <div className="col-md-6 mb-3">
                            <FormControl fullWidth>
                                <TextField
                                    label="Authority"
                                    variant="outlined"
                                    value={authorityLabel}
                                    InputProps={{ readOnly: true }}
                                    disabled
                                />
                            </FormControl>
                        </div>

                        <div className="col-md-6 mb-3">
                            <FormControl fullWidth>
                                <TextField
                                    label="Authority Account"
                                    variant="outlined"
                                    value={accountLabel}
                                    InputProps={{ readOnly: true }}
                                    disabled
                                />
                            </FormControl>
                        </div>

                        <div className="col-md-6 mb-3">
                            <FormControl fullWidth>
                                <TextField
                                    label="Challan Value"
                                    variant="outlined"
                                    value={formatAmount(formData.amount)}
                                    InputProps={{ readOnly: true }}
                                    disabled
                                />
                            </FormControl>
                        </div>

                        <div className="col-md-6 mb-3 d-flex align-items-end">
                            <div className="attachment-download-list w-100">
                                {formData.attachments?.length > 0 ? (
                                    formData.attachments.map((att, index) => {
                                        const url = getAttachmentUrl(att);
                                        if (!url) return null;
                                        return (
                                            <button
                                                key={`attachment_${index}`}
                                                type="button"
                                                className="attachment-download-btn"
                                                onClick={() => handleDownload(url)}
                                            >
                                                <AiOutlineVerticalAlignBottom size={16} />
                                                Attachment {index + 1}
                                            </button>
                                        );
                                    })
                                ) : (
                                    <span className="attachment-download-empty">No attachment</span>
                                )}
                            </div>
                        </div>

                        {authData.user.user_type === 'l2' && (
                            <div className="col-md-12 mb-3">
                                <FormControl fullWidth>
                                    <TextField
                                        label="L2 Remarks"
                                        variant="outlined"
                                        name="l2_remarks"
                                        value={formData.l2_remarks || ''}
                                        onChange={handleChallanFormData}
                                        multiline
                                        minRows={3}
                                        required={!isPaymentView}
                                        disabled={isPaymentView}
                                        error={Boolean(remarksError) && !isPaymentView}
                                        helperText={
                                            isPaymentView
                                                ? ''
                                                : remarksError || 'Required before approve or reject'
                                        }
                                        InputProps={{
                                            readOnly: authData.user.user_type !== 'l2' || isPaymentView,
                                        }}
                                    />
                                </FormControl>
                            </div>
                        )}

                        {authData.user.user_type === 'l3' && (
                            <div className="col-md-12 mb-3">
                                <FormControl fullWidth>
                                    <TextField
                                        label="L3 Remarks"
                                        variant="outlined"
                                        name="l3_remarks"
                                        value={formData.l3_remarks || ''}
                                        onChange={handleChallanFormData}
                                        multiline
                                        minRows={3}
                                        required={!isPaymentView}
                                        disabled={isPaymentView}
                                        error={Boolean(remarksError) && !isPaymentView}
                                        helperText={
                                            isPaymentView
                                                ? ''
                                                : remarksError || 'Required before approve or reject'
                                        }
                                        InputProps={{
                                            readOnly: authData.user.user_type !== 'l3' || isPaymentView,
                                        }}
                                    />
                                </FormControl>
                            </div>
                        )}

                        {showApprovalActions && (
                            <div className="col-md-12">
                                <div className="approval-action-buttons">
                                    <button
                                        type="button"
                                        className="approval-approve-btn d-inline-flex align-items-center justify-content-center gap-2"
                                        onClick={() => previewhandler(true, 1)}
                                    >
                                        <FiCheckCircle size={18} />
                                        Approve
                                    </button>
                                    <button
                                        type="button"
                                        className="approval-reject-btn d-inline-flex align-items-center justify-content-center gap-2"
                                        onClick={() => previewhandler(true, 0)}
                                    >
                                        <FiXCircle size={18} />
                                        Reject
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="col-md-12">
                            {!isPaymentView
                                && ((formData.l2_approval == 1 && authData.user.user_type === 'l2')
                                || (formData.l3_approval == 1 && authData.user.user_type === 'l3')) && (
                                <div className="approval-action-buttons">
                                    <button type="button" className="approval-status-btn approval-status-btn--approved" disabled>
                                        Already Approved
                                    </button>
                                </div>
                            )}
                            {((formData.l2_approval == 0 && authData.user.user_type === 'l2')
                                || (formData.l3_approval == 0 && authData.user.user_type === 'l3')) && (
                                <div className="approval-action-buttons">
                                    <button type="button" className="approval-status-btn approval-status-btn--rejected" disabled>
                                        Already Rejected
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {authData.user.user_type !== 'l1'
                        && formData.l2_approval == 1
                        && formData.l3_approval == 1 && (
                        <div className="mt-4">
                            <LabourPayment data={formData} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ViewLabourCess;
